import { AlertCircle, Activity, RefreshCw } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaPagination } from "@/shared/components/navigation/ColocaPagination";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { INTERNAL_API_KEY_USAGE_PAGE_SIZE } from "../constants/internalApiKeys.constants";
import { useInternalApiKeyUsage } from "../hooks/useInternalApiKeyUsage";
import type { InternalApiKey } from "../types/internalApiKeys.types";

export interface InternalApiKeyUsageDrawerProps {
  apiKey: InternalApiKey | null;
  onClose: () => void;
}

/** Every request that arrived with the key, refusals with their reason — the 404 the caller saw says nothing. */
export function InternalApiKeyUsageDrawer({ apiKey, onClose }: InternalApiKeyUsageDrawerProps) {
  const { t, activeLanguage } = useTranslations("internalApiKeys");
  const usage = useInternalApiKeyUsage(apiKey?.id ?? null);

  return (
    <ColocaDrawer
      open={apiKey !== null}
      onOpenChange={(next) => !next && onClose()}
      size="lg"
      pinLastChild={false}
      title={t("usage_title", { name: apiKey?.name ?? "" })}
      description={t("usage_description")}
    >
      {usage.error ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={usage.error}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={usage.reload}>
              {t("action_retry")}
            </ColocaButton>
          }
        />
      ) : usage.isLoading && usage.entries.length === 0 ? (
        <ColocaSkeleton className="h-48 w-full rounded-xl" />
      ) : usage.entries.length === 0 ? (
        <ColocaEmpty icon={Activity} title={t("usage_empty")} surface="card" />
      ) : (
        <div className="space-y-3">
          <ul className="divide-y divide-border rounded-xl border border-border">
            {usage.entries.map((entry) => (
              <li key={entry.id} className="space-y-1 px-4 py-3">
                <div className="flex flex-wrap items-center gap-2">
                  <ColocaStatusPill variant={entry.outcome === "OK" ? "success" : "danger"}>
                    {t(`outcome_${entry.outcome}`)}
                  </ColocaStatusPill>
                  <span className="text-xs text-muted-foreground">
                    {t("usage_status", { code: entry.status_code })}
                  </span>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {formatDateTime(entry.created_at, { locale: activeLanguage, includeTime: true })}
                  </span>
                </div>
                <p className="truncate font-mono text-xs text-foreground">
                  {entry.method} {entry.route}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {[entry.ip_address, entry.user_agent].filter(Boolean).join(" · ")}
                </p>
              </li>
            ))}
          </ul>
          <ColocaPagination
            page={usage.page}
            perPage={INTERNAL_API_KEY_USAGE_PAGE_SIZE}
            total={usage.total}
            onPageChange={usage.setPage}
            size="sm"
          />
        </div>
      )}
    </ColocaDrawer>
  );
}
